import { createContext, useContext, useEffect, useCallback, useRef, useState } from 'react'
import type { ReactNode, CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { X, Maximize2, Minimize2, ChevronLeft } from 'lucide-react'

/**
 * Opt-in IN-PLACE presentation. A screen that wants the dialogs opened inside
 * one of its panels (the Transaction Designer's inspector) provides a host
 * element here; every Modal rendered beneath the provider then draws into that
 * element as a drill-in panel — "‹ back" header, scrolling body, the same
 * footer buttons — instead of over the page. Nothing changes for a Modal with
 * no provider above it. `onOpenChange` lets the host widen itself while one is
 * showing.
 */
export interface ModalInlineHost {
  target: HTMLElement | null
  onOpenChange?: (delta: 1 | -1) => void
}
export const ModalInlineHostContext = createContext<ModalInlineHost | null>(null)

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'default' | 'wide' | 'full'
  // Opt-in enhancements (default off → other modals are unchanged):
  //   resizable   → drag the bottom-right corner to any size
  //   maximizable → a header button toggles near-full-screen
  //   storageKey  → persist {width, height, maximized} across sessions
  // When either resizable or maximizable is set the card becomes a fixed-size
  // flex column and the body area scrolls internally, so children should use
  // flex:1/minHeight:0 for their own scroll region.
  resizable?: boolean
  maximizable?: boolean
  storageKey?: string
}

const MIN_W = 480
const MIN_H = 360
const MAXIMIZE_W = '96vw'
const MAXIMIZE_H = '94vh'

const SIZE_CAP = { default: 560, wide: 720, full: 960 } as const

interface Dims { width: number; height: number; maximized: boolean }

function loadDims(storageKey: string | undefined, size: keyof typeof SIZE_CAP): Dims {
  const defW = Math.min(SIZE_CAP[size], Math.round(window.innerWidth * 0.9))
  const defH = Math.round(window.innerHeight * 0.72)
  const fallback: Dims = { width: defW, height: defH, maximized: false }
  if (!storageKey) return fallback
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return fallback
    const saved = JSON.parse(raw) as Partial<Dims>
    return {
      width: typeof saved.width === 'number'
        ? Math.max(MIN_W, Math.min(saved.width, window.innerWidth)) : defW,
      height: typeof saved.height === 'number'
        ? Math.max(MIN_H, Math.min(saved.height, window.innerHeight)) : defH,
      maximized: !!saved.maximized,
    }
  } catch {
    return fallback
  }
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'default',
  resizable = false,
  maximizable = false,
  storageKey,
}: ModalProps) {
  const enhanced = resizable || maximizable
  const inlineHost = useContext(ModalInlineHostContext)
  const inline = !!inlineHost?.target
  // Tell the host while an in-place panel is showing (it widens itself).
  useEffect(() => {
    if (!open || !inline || !inlineHost?.onOpenChange) return
    inlineHost.onOpenChange(1)
    return () => inlineHost.onOpenChange?.(-1)
  }, [open, inline, inlineHost])

  // Seeded fresh on each open (the component unmounts when closed), so the
  // last-used size/maximized state is restored from localStorage every time.
  const [dims, setDims] = useState<Dims>(() => loadDims(storageKey, size))
  const dragRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null)

  const persist = useCallback((d: Dims) => {
    if (!storageKey) return
    try {
      localStorage.setItem(storageKey, JSON.stringify(d))
    } catch {
      /* localStorage may be unavailable (private mode) — size just won't persist. */
    }
  }, [storageKey])

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (open) {
      // Re-seed from storage when (re)opening.
      if (enhanced) setDims(loadDims(storageKey, size))
      document.addEventListener('keydown', handleKeyDown)
      if (!inline) document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, handleKeyDown])

  const toggleMaximized = useCallback(() => {
    setDims((d) => {
      const next = { ...d, maximized: !d.maximized }
      persist(next)
      return next
    })
  }, [persist])

  // Corner-drag resize. Bound to window so the pointer can leave the handle.
  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { startX: e.clientX, startY: e.clientY, startW: dims.width, startH: dims.height }
    const onMove = (ev: MouseEvent) => {
      const s = dragRef.current
      if (!s) return
      const width = Math.max(MIN_W, Math.min(s.startW + (ev.clientX - s.startX), window.innerWidth - 24))
      const height = Math.max(MIN_H, Math.min(s.startH + (ev.clientY - s.startY), window.innerHeight - 24))
      setDims((d) => ({ ...d, width, height }))
    }
    const onUp = () => {
      dragRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.userSelect = ''
      setDims((d) => { persist(d); return d })
    }
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [dims.width, dims.height, persist])

  if (!open) return null

  if (inlineHost?.target) {
    return createPortal(
      <div
        role="dialog"
        aria-label={title || 'Edit'}
        style={{
          position: 'absolute', inset: 0, zIndex: 5, display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg-secondary)', pointerEvents: 'auto',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px',
          borderBottom: '1px solid var(--color-border)', flexShrink: 0,
        }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Back"
            title="Back (Esc)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 2, paddingLeft: 4 }}
          >
            <ChevronLeft size={16} /> Back
          </button>
          {title && (
            <h3 style={{
              margin: 0, fontSize: '1rem', fontWeight: 600, flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{title}</h3>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
        {footer && (
          <div className="modal-footer" style={{ flexShrink: 0, padding: '10px 12px', margin: 0, borderTop: '1px solid var(--color-border)' }}>
            {footer}
          </div>
        )}
      </div>,
      inlineHost.target,
    )
  }

  const modalStyle: CSSProperties = enhanced
    ? (dims.maximized
        ? { maxWidth: 'none', width: MAXIMIZE_W, height: MAXIMIZE_H, maxHeight: 'none',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }
        : { maxWidth: 'none', width: dims.width, height: dims.height, maxHeight: 'none',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' })
    : { maxWidth: SIZE_CAP[size] }

  const maxBtn = maximizable && (
    <button
      className="modal-close"
      onClick={toggleMaximized}
      aria-label={dims.maximized ? 'Restore' : 'Maximize'}
      title={dims.maximized ? 'Restore' : 'Maximize'}
    >
      {dims.maximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
    </button>
  )

  return (
    <div
      className="overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="modal animate-slide-up"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div className="modal-header" style={enhanced ? { flexShrink: 0 } : undefined}>
            <h3 className="modal-title">{title}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {maxBtn}
              <button
                className="modal-close"
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Close button without title */}
        {!title && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginBottom: '0.5rem', flexShrink: 0 }}>
            {maxBtn}
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Body */}
        <div
          style={enhanced
            // overflow:visible so a focused input's outline/ring isn't clipped
            // at the flush left edge — the child scroll region owns scrolling.
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'visible' }
            : { marginBottom: footer ? '0' : undefined }}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && <div className="modal-footer" style={enhanced ? { flexShrink: 0 } : undefined}>{footer}</div>}

        {/* Resize handle (bottom-right) — hidden while maximized. */}
        {resizable && !dims.maximized && (
          <div
            onMouseDown={onResizeStart}
            title="Drag to resize"
            style={{
              position: 'absolute',
              right: 2,
              bottom: 2,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              // Two diagonal grip lines drawn with a repeating gradient.
              background:
                'linear-gradient(135deg, transparent 0 45%, var(--color-text-secondary) 45% 55%, transparent 55% 70%, var(--color-text-secondary) 70% 80%, transparent 80%)',
              opacity: 0.5,
              borderBottomRightRadius: 'var(--radius-md)',
            }}
          />
        )}
      </div>
    </div>
  )
}
